import type { AppEvent, Workspace, Project, Board, BoardDetail, Card, Label, Comment, Repo, ViewConfig } from '../types.ts';
import { api } from '../api.ts';

/**
 * Per-workspace session metadata (Round 7). Tracks the state of every
 * workspace the user has opened this session, even if it's not the
 * currently-active one. Used by the NavRail workspace switcher and
 * by the background-notification router.
 *
 * `runningAgents` is kept as an event counter (incremented on
 * agent_spawned, decremented on task_completed/task_failed) so we
 * can trigger "all agents done" notifications for background
 * workspaces without full state replay.
 */
export interface SessionMeta {
  status:         'idle' | 'loading' | 'agent-running' | 'agent-just-finished' | 'error';
  runningAgents:  number;
  unreadCount:    number;
  lastActiveAt:   number;  // ms epoch
}

function emptyMeta(): SessionMeta {
  return { status: 'idle', runningAgents: 0, unreadCount: 0, lastActiveAt: Date.now() };
}

const OPEN_WS_KEY   = 'agentboard.openWorkspaceIds';
const ACTIVE_WS_KEY = 'agentboard.activeWorkspaceId';

function loadOpenWorkspaceIds(): string[] {
  try {
    const raw = localStorage.getItem(OPEN_WS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveOpenWorkspaceIds(ids: string[]) {
  try { localStorage.setItem(OPEN_WS_KEY, JSON.stringify(ids)); } catch {}
}

function loadActiveWorkspaceId(): string | null {
  try { return localStorage.getItem(ACTIVE_WS_KEY); } catch { return null; }
}

export function saveActiveWorkspaceId(id: string | null) {
  try {
    if (id) localStorage.setItem(ACTIVE_WS_KEY, id);
    else localStorage.removeItem(ACTIVE_WS_KEY);
  } catch {}
}

export const pmStore = $state({
  workspaces:        [] as Workspace[],
  projects:          [] as Project[],
  repos:             [] as Repo[],
  boards:            [] as Board[],
  boardDetails:      {} as Record<string, BoardDetail>,
  activeWorkspaceId: null as string | null,
  activeProjectId:   null as string | null,
  activeBoardId:     null as string | null,
  cardLabels:        {} as Record<string, Label[]>,
  cardComments:      {} as Record<string, Comment[]>,
  viewConfigs:       [] as ViewConfig[],

  // ── Round 7: multi-workspace session persistence ─────────────
  // The ordered list of workspaces the user has opened this session.
  // Restored from localStorage on boot. The currently-active workspace
  // is always present in this list.
  openWorkspaceIds:  loadOpenWorkspaceIds() as string[],
  // Per-workspace session metadata keyed by workspace id. Updated by
  // the WebSocket handler for every open workspace, not just the
  // active one — that's what powers the background-agent status
  // chips and notifications.
  sessionMeta:       {} as Record<string, SessionMeta>,
});

/** Return (creating if needed) the session metadata for a workspace. */
export function getOrCreateMeta(workspaceId: string): SessionMeta {
  let m = pmStore.sessionMeta[workspaceId];
  if (!m) {
    m = emptyMeta();
    pmStore.sessionMeta[workspaceId] = m;
  }
  return m;
}

/** Add a workspace to the open-sessions list and mark it active. */
export function openWorkspaceSession(workspaceId: string): void {
  if (!pmStore.openWorkspaceIds.includes(workspaceId)) {
    pmStore.openWorkspaceIds = [...pmStore.openWorkspaceIds, workspaceId];
    saveOpenWorkspaceIds(pmStore.openWorkspaceIds);
  }
  getOrCreateMeta(workspaceId).lastActiveAt = Date.now();
  // Opening a session clears its unread badge because the user is
  // about to look at it.
  getOrCreateMeta(workspaceId).unreadCount = 0;
}

/** Remove a workspace from the open-sessions list. If it was active,
 *  pick the next session (or null) as the new active. */
export function closeWorkspaceSession(workspaceId: string): string | null {
  const next = pmStore.openWorkspaceIds.filter((id) => id !== workspaceId);
  pmStore.openWorkspaceIds = next;
  saveOpenWorkspaceIds(next);
  delete pmStore.sessionMeta[workspaceId];
  if (pmStore.activeWorkspaceId === workspaceId) {
    const fallback = next[0] ?? null;
    pmStore.activeWorkspaceId = fallback;
    saveActiveWorkspaceId(fallback);
    return fallback;
  }
  return pmStore.activeWorkspaceId;
}

export async function hydrateWorkspaces(): Promise<void> {
  try {
    let ws = await api<Workspace[]>('/api/v1/workspaces');

    // First-run bootstrap: seed a realistic demo workspace so the app
    // doesn't open into empty shells on a fresh database.
    if (ws.length === 0) {
      const { seedDemoWorkspace } = await import('../seed-demo.ts');
      await seedDemoWorkspace({ force: true });
      ws = await api<Workspace[]>('/api/v1/workspaces');
      if (ws.length === 0) {
        try {
          await api<Workspace>('/api/v1/workspaces', {
            method: 'POST',
            body: JSON.stringify({ name: 'Personal', slug: 'personal' }),
          });
          ws = await api<Workspace[]>('/api/v1/workspaces');
        } catch (e) {
          console.warn('Failed to bootstrap default workspace:', e);
        }
      }
    }

    pmStore.workspaces = ws;

    // Prune any restored session ids that no longer correspond to a
    // real workspace (deleted, permissions revoked, etc).
    const validIds = new Set(ws.map((w) => w.id));
    pmStore.openWorkspaceIds = pmStore.openWorkspaceIds.filter((id) => validIds.has(id));
    saveOpenWorkspaceIds(pmStore.openWorkspaceIds);

    // Ensure each open session has a meta entry.
    for (const id of pmStore.openWorkspaceIds) getOrCreateMeta(id);

    if (ws.length > 0 && !pmStore.activeWorkspaceId) {
      // Restore the last-active workspace from localStorage, then fall back
      // to the first open-session id, then the first workspace.
      const persisted = loadActiveWorkspaceId();
      const first = (persisted && validIds.has(persisted) ? persisted : null)
        ?? pmStore.openWorkspaceIds[0]
        ?? ws[0].id;
      pmStore.activeWorkspaceId = first;
      saveActiveWorkspaceId(first);
      openWorkspaceSession(first);
      await Promise.all([hydrateProjects(first), hydrateRepos(first), hydrateViewConfigs(first)]);
    }
  } catch (e) {
    console.warn('Failed to hydrate workspaces:', e);
  }
}

export async function hydrateRepos(workspaceId: string): Promise<void> {
  try {
    const repos = await api<Repo[]>(`/api/v1/workspaces/${workspaceId}/repos`);
    pmStore.repos = repos;
  } catch (e) {
    console.warn('Failed to hydrate repos:', e);
  }
}

export async function hydrateViewConfigs(workspaceId: string): Promise<void> {
  try {
    const configs = await api<ViewConfig[]>(`/api/v1/workspaces/${workspaceId}/view-configs`);
    pmStore.viewConfigs = configs;
  } catch (e) {
    console.warn('Failed to hydrate view configs:', e);
  }
}

export async function hydrateProjects(workspaceId: string): Promise<void> {
  try {
    const projects = await api<Project[]>(`/api/v1/workspaces/${workspaceId}/projects`);
    pmStore.projects = projects;
    if (projects.length > 0) {
      await hydrateBoards(projects[0].id);
    }
  } catch (e) {
    console.warn('Failed to hydrate projects:', e);
  }
}

export async function hydrateBoards(projectId: string): Promise<void> {
  pmStore.activeProjectId = projectId;
  try {
    const boards = await api<Board[]>(`/api/v1/projects/${projectId}/boards`);
    pmStore.boards = boards;
    if (boards.length > 0) {
      pmStore.activeBoardId = boards[0].id;
      await hydrateBoardDetail(boards[0].id);
    }
  } catch (e) {
    console.warn('Failed to hydrate boards:', e);
  }
}

export async function hydrateBoardDetail(boardId: string): Promise<void> {
  pmStore.activeBoardId = boardId;
  try {
    const detail = await api<BoardDetail>(`/api/v1/boards/${boardId}`);
    pmStore.boardDetails[boardId] = detail;
  } catch (e) {
    console.warn('Failed to hydrate board detail:', e);
  }
}

export function applyPmEvent(event: AppEvent): void {
  switch (event.event_type) {
    case 'workspace_created': {
      const payload = event.payload as { name: string; slug: string };
      if (!pmStore.workspaces.find(w => w.id === event.agent_id)) {
        pmStore.workspaces.push({
          id: event.agent_id,
          name: payload.name,
          slug: payload.slug,
          created_at: event.created_at,
        });
      }
      break;
    }
    case 'project_created': {
      const payload = event.payload as { workspace_id: string; name: string; slug: string };
      if (!pmStore.projects.find(p => p.id === event.agent_id)) {
        pmStore.projects.push({
          id: event.agent_id,
          workspace_id: payload.workspace_id,
          name: payload.name,
          slug: payload.slug,
          description: null,
          created_at: event.created_at,
          updated_at: event.created_at,
          repo_id: null,
        });
      }
      break;
    }
    case 'board_created': {
      const payload = event.payload as { project_id: string; name: string };
      if (!pmStore.boards.find(b => b.id === event.agent_id)) {
        pmStore.boards.push({
          id: event.agent_id,
          project_id: payload.project_id,
          name: payload.name,
          created_at: event.created_at,
        });
      }
      void hydrateBoardDetail(event.agent_id);
      break;
    }
    case 'card_created': {
      const payload = event.payload as { project_id: string; column_id: string | null; title: string; priority: string };
      const board = Object.values(pmStore.boardDetails).find(bd =>
        bd.columns.some(c => c.id === payload.column_id)
      );
      if (board) {
        const col = board.columns.find(c => c.id === payload.column_id);
        if (col) {
          const newCard: Card = {
            id: event.agent_id,
            project_id: payload.project_id,
            column_id: payload.column_id,
            // The card_created event now includes human_id on the payload
            // (emit site in crates/agentboard-pm/src/sqlite.rs). Older
            // clients/servers that don't will leave it null and the UI
            // falls back to the UUID short form.
            human_id: (payload as { human_id?: string }).human_id ?? null,
            title: payload.title,
            description: null,
            priority: payload.priority as Card['priority'],
            agent_id: null,
            position: col.cards.length,
            due_date: null,
            created_at: event.created_at,
            updated_at: event.created_at,
            loop_mode: false,
            loop_max: 5,
            loop_count: 0,
            loop_criteria: null,
            worktree_path: null,
            parent_id: null,
            assignee_kind: null,
            assignee_id: null,
          };
          col.cards.push(newCard);
        }
      }
      break;
    }
    case 'card_updated': {
      const payload = event.payload as {
        title?: string;
        description?: string | null;
        priority?: string;
        column_id?: string | null;
        position?: number;
        due_date?: string | null;
      };
      for (const bd of Object.values(pmStore.boardDetails)) {
        for (const col of bd.columns) {
          const card = col.cards.find(c => c.id === event.agent_id);
          if (card) {
            if (payload.title       !== undefined) card.title       = payload.title;
            if (payload.description !== undefined) card.description = payload.description ?? null;
            if (payload.priority    !== undefined) card.priority    = payload.priority as Card['priority'];
            if (payload.due_date    !== undefined) card.due_date    = payload.due_date ?? null;
            card.updated_at = event.created_at;
          }
        }
      }
      break;
    }
    case 'card_moved': {
      const payload = event.payload as { from_column_id: string; to_column_id: string; position: number };
      for (const bd of Object.values(pmStore.boardDetails)) {
        const fromCol = bd.columns.find(c => c.id === payload.from_column_id);
        const toCol   = bd.columns.find(c => c.id === payload.to_column_id);
        if (fromCol && toCol) {
          const idx = fromCol.cards.findIndex(c => c.id === event.agent_id);
          if (idx !== -1) {
            const [card] = fromCol.cards.splice(idx, 1);
            card.column_id = payload.to_column_id;
            card.position  = payload.position;
            toCol.cards.splice(payload.position, 0, card);
          }
        }
      }
      break;
    }
    case 'card_deleted': {
      for (const bd of Object.values(pmStore.boardDetails)) {
        for (const col of bd.columns) {
          col.cards = col.cards.filter(c => c.id !== event.agent_id);
        }
      }
      break;
    }
    case 'column_created': {
      const payload = event.payload as { board_id: string; name: string; color: string; position: number };
      const bd = pmStore.boardDetails[payload.board_id];
      if (bd) {
        // Only add if not already present (idempotent)
        if (!bd.columns.find(c => c.id === event.agent_id)) {
          bd.columns.push({
            id:         event.agent_id,
            board_id:   payload.board_id,
            name:       payload.name,
            color:      payload.color,
            position:   payload.position,
            created_at: event.created_at,
            cards:      [],
          });
          // Keep columns sorted by position
          bd.columns.sort((a, b) => a.position - b.position);
        }
      }
      break;
    }
    case 'label_created': {
      // Labels are fetched on demand when CardPanel opens.
      // No local cache update needed here.
      break;
    }
    case 'card_label_added': {
      const payload = event.payload as { card_id: string; label_id: string };
      // Invalidate so CardPanel re-fetches
      delete pmStore.cardLabels[payload.card_id];
      break;
    }
    case 'card_label_removed': {
      const payload = event.payload as { card_id: string; label_id: string };
      const existing = pmStore.cardLabels[payload.card_id];
      if (existing) {
        pmStore.cardLabels[payload.card_id] = existing.filter(l => l.id !== payload.label_id);
      }
      break;
    }
    case 'comment_added': {
      const payload = event.payload as { card_id: string };
      // Invalidate so CardPanel re-fetches
      delete pmStore.cardComments[payload.card_id];
      break;
    }
    case 'comment_deleted': {
      const payload = event.payload as { card_id: string };
      delete pmStore.cardComments[payload.card_id];
      break;
    }
  }
}
